import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsDark } from '@/shared/theme/useThemeColors';
import { ROUTES } from '@/shared/config/constants';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { useDeploymentDetail } from '@/features/deployments/hooks/useDeployments';
import { usePermissions } from '@/entities/rbac/hooks/usePermissions';

const TOPBAR_H = 56;
const SIDEBAR_W = 220;
const SIDEBAR_COLLAPSED_W = 52;

const NAV_ITEMS = [
  { key: 'deployments', label: 'Deployments', icon: 'hub', path: ROUTES.DEPLOYMENTS },
  { key: 'users', label: 'Users', icon: 'group', path: ROUTES.USERS, ripikOnly: true },
  { key: 'settings', label: 'Settings', icon: 'settings', path: ROUTES.SETTINGS, ripikOnly: true },
];

const DEPLOYMENT_SUBNAV = [
  { key: 'live',            label: 'Live · Recommended', icon: 'recommend' },
  { key: 'terminal',        label: 'Live · Terminal',  icon: 'terminal' },
  { key: 'consensus',       label: 'Live · Consensus', icon: 'handshake' },
  { key: 'broadcast',       label: 'Live · News',      icon: 'live_tv' },
  { key: 'command',         label: 'Live · HUD',       icon: 'space_dashboard' },
  { key: 'board',           label: 'Live · Board',     icon: 'departure_board' },
  { key: 'overview',        label: 'Overview',        icon: 'home' },
  { key: 'dashboard',       label: 'All Parameters',  icon: 'dashboard' },
  { key: 'recommendations', label: 'Recommendations', icon: 'lightbulb' },
  { key: 'alerts',          label: 'Alerts',          icon: 'notifications_active' },
  { key: 'simulation',      label: 'Simulation',      icon: 'science' },
  { key: 'users',           label: 'Users',           icon: 'group',   settingsOnly: true },
  { key: 'parameters',      label: 'Parameters',      icon: 'tune',    settingsOnly: true },
  { key: 'layers',          label: 'Layers',          icon: 'layers',  settingsOnly: true },
  { key: 'notifications',   label: 'Notifications',   icon: 'notifications', settingsOnly: true },
  { key: 'shifts',          label: 'Settings',        icon: 'settings', settingsOnly: true },
];

function SubNavItem({ item, activeDepId, activeSection, location, collapsed, colors, hoveredItem, setHoveredItem, navigate }) {
  const isSettingsSection = item.settingsOnly;
  const isOwnRoute = !item.settingsOnly; // dashboard + recommendations have their own routes
  const sectionKey = item.section ?? item.key;
  const active = isSettingsSection
    ? activeSection === sectionKey && location.pathname.includes('/settings')
    : location.pathname.includes(`/${item.key}`);
  const hovered = hoveredItem === `sub-${item.key}`;

  function handleClick() {
    if (isOwnRoute) {
      navigate(`/deployments/${activeDepId}/${item.key}`);
    } else {
      navigate(`/deployments/${activeDepId}/settings?section=${sectionKey}`);
    }
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHoveredItem(`sub-${item.key}`)}
      onMouseLeave={() => setHoveredItem(null)}
      title={collapsed ? item.label : undefined}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        paddingLeft: collapsed ? '10px' : '20px',
        height: '40px',
        boxSizing: 'border-box',
        borderRadius: radius.lg,
        cursor: 'pointer',
        background: active ? colors.activeGrad : hovered ? colors.hover : 'transparent',
        border: active ? `1px solid ${colors.activeBorder}` : '1px solid transparent',
        marginBottom: '1px',
        transition: 'background 0.15s, border-color 0.15s, padding 0.2s cubic-bezier(0.4,0,0.2,1)',
        justifyContent: 'flex-start',
      }}
    >
      {active && (
        <div style={{
          position: 'absolute',
          left: 0, top: '20%', bottom: '20%',
          width: '3px', borderRadius: '0 3px 3px 0',
          background: colors.activeIndicator,
        }} />
      )}
      <span
        className="material-symbols-outlined"
        style={{
          fontSize: 16,
          color: active ? colors.activeText : hovered ? colors.activeText : colors.subtext,
          fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0",
          flexShrink: 0,
          transition: 'color 0.15s',
        }}
      >
        {item.icon}
      </span>
      <span style={{
        fontSize: typography.fontSize.base,
        fontWeight: active ? 600 : 400,
        color: active ? colors.activeText : hovered ? colors.activeText : colors.text,
        fontFamily: typography.fontFamily.body,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        opacity: collapsed ? 0 : 1,
        maxWidth: collapsed ? 0 : '160px',
        transition: 'opacity 0.15s, max-width 0.2s cubic-bezier(0.4,0,0.2,1), color 0.15s',
      }}>
        {item.label}
      </span>
    </div>
  );
}

function useActiveDeploymentId() {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/deployments\/([^/]+)/);
  return match ? match[1] : null;
}

function useActiveSection() {
  const { pathname, search } = useLocation();
  if (pathname.includes('/live')) return 'live';
  if (pathname.includes('/overview')) return 'overview';
  if (pathname.includes('/dashboard')) return 'dashboard';
  if (pathname.includes('/recommendations')) return 'recommendations';
  if (pathname.includes('/alerts')) return 'alerts';
  if (pathname.includes('/simulation')) return 'simulation';
  const params = new URLSearchParams(search);
  return params.get('section') ?? 'users';
}

export function Sidebar({ collapsed, onToggle, mobileOpen = false, isMobile = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = useIsDark();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverToggle, setHoverToggle] = useState(false);
  const activeDepId = useActiveDeploymentId();
  const activeSection = useActiveSection();
  useDeploymentDetail(activeDepId);
  const { isRipikAdmin, canViewSettings } = usePermissions();

  const colors = {
    bg: isDark ? '#141927' : '#ffffff',
    border: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(30,67,150,0.08)',
    text: isDark ? '#e2e8f0' : '#191c1e',
    subtext: isDark ? '#a0aec0' : '#94a3b8',
    activeGrad: isDark
      ? 'linear-gradient(135deg, rgba(32,36,117,0.3), rgba(62,181,231,0.15))'
      : 'linear-gradient(135deg, rgba(32,36,117,0.07), rgba(62,181,231,0.06))',
    activeBorder: isDark ? 'rgba(62,181,231,0.25)' : 'rgba(62,181,231,0.15)',
    activeIndicator: 'linear-gradient(180deg, #202475, #3EB5E7)',
    activeText: isDark ? '#e2e8f0' : semantic.brandNavyMid,
    hover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(30,67,150,0.04)',
    toggleHover: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(30,67,150,0.06)',
  };

  const w = collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  function isActive(item) {
    return location.pathname.startsWith(item.path);
  }

  return (
    <aside style={{
      position: 'fixed',
      top: isMobile ? 0 : TOPBAR_H,
      left: isMobile ? (mobileOpen ? 0 : -SIDEBAR_W) : 0,
      bottom: 0,
      width: isMobile ? SIDEBAR_W : w,
      background: colors.bg,
      borderRight: `1px solid ${colors.border}`,
      display: 'flex',
      flexDirection: 'column',
      transition: isMobile
        ? 'left 0.25s cubic-bezier(0.4,0,0.2,1)'
        : 'width 0.2s cubic-bezier(0.4,0,0.2,1)',
      zIndex: 200,
      overflow: 'hidden',
      boxShadow: isMobile && mobileOpen ? '4px 0 24px rgba(0,0,0,0.18)' : 'none',
    }}>
      {/* Gradient accent bar */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #202475 0%, #1e4396 40%, #3EB5E7 100%)', flexShrink: 0 }} />

      {/* Collapse toggle — top of sidebar */}
      <div style={{
        padding: collapsed ? '6px 0' : '6px 8px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        justifyContent: collapsed ? 'center' : 'flex-end',
        flexShrink: 0,
      }}>
        <button
          onClick={onToggle}
          onMouseEnter={() => setHoverToggle(true)}
          onMouseLeave={() => setHoverToggle(false)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: radius.md,
            background: hoverToggle ? colors.toggleHover : 'transparent',
            border: `1px solid ${hoverToggle ? colors.border : 'transparent'}`,
            cursor: 'pointer', transition: 'all 0.15s', padding: 0,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 15, color: colors.subtext }}>
            {collapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
          </span>
        </button>
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '8px 8px', overflowY: 'auto', overflowX: 'hidden' }}>
        {NAV_ITEMS.filter((item) => {
          if (item.ripikOnly && !isRipikAdmin) return false;
          if (activeDepId) return false;
          return true;
        }).map((item) => {
          const active = isActive(item);
          const hovered = hoveredItem === item.key;
          return (
            <div
              key={item.key}
              onClick={() => navigate(item.path)}
              onMouseEnter={() => setHoveredItem(item.key)}
              onMouseLeave={() => setHoveredItem(null)}
              title={collapsed ? item.label : undefined}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: collapsed ? '0 0 0 10px' : '9px 12px',
                height: '40px',
                boxSizing: 'border-box',
                borderRadius: radius.lg,
                cursor: 'pointer',
                background: active ? colors.activeGrad : hovered ? colors.hover : 'transparent',
                border: active ? `1px solid ${colors.activeBorder}` : '1px solid transparent',
                marginBottom: '2px',
                transition: 'background 0.15s, border-color 0.15s, padding 0.2s cubic-bezier(0.4,0,0.2,1)',
                justifyContent: 'flex-start',
                overflow: 'hidden',
              }}
            >
              {active && (
                <div style={{
                  position: 'absolute',
                  left: 0, top: '20%', bottom: '20%',
                  width: '3px', borderRadius: '0 3px 3px 0',
                  background: colors.activeIndicator,
                }} />
              )}

              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 18,
                  color: active ? colors.activeText : hovered ? colors.activeText : colors.subtext,
                  fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0",
                  flexShrink: 0,
                  transition: 'color 0.15s',
                }}
              >
                {item.icon}
              </span>

              <span style={{
                fontSize: typography.fontSize.md,
                fontWeight: active ? 600 : 400,
                color: active ? colors.activeText : hovered ? colors.activeText : colors.text,
                fontFamily: typography.fontFamily.body,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                opacity: collapsed ? 0 : 1,
                maxWidth: collapsed ? 0 : '160px',
                transition: 'opacity 0.15s, max-width 0.2s cubic-bezier(0.4,0,0.2,1), color 0.15s',
              }}>
                {item.label}
              </span>
              <span className="material-symbols-outlined" style={{
                fontSize: 14,
                color: semantic.brandAccent,
                flexShrink: 0,
                opacity: active && !collapsed ? 1 : 0,
                maxWidth: active && !collapsed ? '14px' : 0,
                overflow: 'hidden',
                transition: 'opacity 0.15s, max-width 0.2s cubic-bezier(0.4,0,0.2,1)',
              }}>
                chevron_right
              </span>
            </div>
          );
        })}

        {/* Deployment sub-nav — shown when inside a deployment */}
        {activeDepId && (
          <div style={{ marginTop: '4px' }}>
            {/* Back button — same bordered icon button in both states */}
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', marginBottom: '4px', padding: collapsed ? '0' : '0 4px' }}>
              <button
                onClick={() => navigate(ROUTES.DEPLOYMENTS, { state: { noRedirect: true } })}
                title="All Deployments"
                style={{
                  height: 32, borderRadius: radius.md,
                  background: 'none', border: `1px solid ${colors.border}`,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '6px',
                  width: collapsed ? 32 : '100%',
                  flexShrink: 0,
                  padding: collapsed ? 0 : '0 10px',
                  color: colors.subtext, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
                  fontFamily: typography.fontFamily.body,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 15, color: colors.subtext, flexShrink: 0 }}>arrow_back</span>
                <span style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  opacity: collapsed ? 0 : 1,
                  maxWidth: collapsed ? 0 : '160px',
                  transition: 'opacity 0.15s, max-width 0.2s cubic-bezier(0.4,0,0.2,1)',
                }}>All Deployments</span>
              </button>
            </div>

            <div style={{ margin: '0 4px 4px', height: '1px', background: colors.border }} />

            {/* User section label — always rendered, invisible when collapsed */}
            {canViewSettings(activeDepId) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0 2px', padding: '0 8px', height: '14px', visibility: collapsed ? 'hidden' : 'visible' }}>
                <div style={{ flex: 1, height: '1px', background: colors.border }} />
                <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: colors.subtext, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>User</span>
                <div style={{ flex: 1, height: '1px', background: colors.border }} />
              </div>
            )}

            {/* User nav items */}
            {DEPLOYMENT_SUBNAV.filter((item) => !item.settingsOnly).map((item) => (
              <SubNavItem
                key={item.key}
                item={item}
                activeDepId={activeDepId}
                activeSection={activeSection}
                location={location}
                collapsed={collapsed}
                colors={colors}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                navigate={navigate}
              />
            ))}

            {/* Admin items — only for users with settings access */}
            {canViewSettings(activeDepId) && (
              <>
                {/* Admin label — always rendered, invisible when collapsed */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0 2px', padding: '0 8px', height: '14px', visibility: collapsed ? 'hidden' : 'visible' }}>
                  <div style={{ flex: 1, height: '1px', background: colors.border }} />
                  <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: colors.subtext, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Admin</span>
                  <div style={{ flex: 1, height: '1px', background: colors.border }} />
                </div>
                {DEPLOYMENT_SUBNAV.filter((item) => item.settingsOnly).map((item) => (
                  <SubNavItem
                    key={item.key}
                    item={item}
                    activeDepId={activeDepId}
                    activeSection={activeSection}
                    location={location}
                    collapsed={collapsed}
                    colors={colors}
                    hoveredItem={hoveredItem}
                    setHoveredItem={setHoveredItem}
                    navigate={navigate}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </nav>
    </aside>
  );
}
